import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { isNdtLogRejected } from '../../../utils/fetchNdtIndicationLogsApi';
import type { NdtIndicationRow } from './drillDownTypes';
import { NdtIndicationDetailPanel } from './NdtIndicationDetailPanel';

interface NdtIndicationDrillDownHostProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  weldNumber: string;
  jobNumber: string;
  logs: NdtIndicationRow[];
}

export function ndtIndicationRowKey(log: NdtIndicationRow, index: number): string {
  return String(log.id ?? `${log.date ?? ''}::${log.passName ?? ''}::${log.defectType ?? ''}::${index}`);
}

function text(value: string | number | null | undefined): string {
  const shown = value == null ? '' : String(value).trim();
  return shown || '—';
}

export const NdtIndicationDrillDownHost = ({
  open,
  onOpenChange,
  weldNumber,
  jobNumber,
  logs,
}: NdtIndicationDrillDownHostProps) => {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSelectedKey(null);
      return;
    }
    setSelectedKey((prev) => {
      if (prev && logs.some((log, index) => ndtIndicationRowKey(log, index) === prev)) return prev;
      return logs[0] ? ndtIndicationRowKey(logs[0], 0) : null;
    });
  }, [open, logs]);

  const selectedIndex = logs.findIndex((log, index) => ndtIndicationRowKey(log, index) === selectedKey);
  const selected = selectedIndex >= 0 ? logs[selectedIndex] : null;
  const rejectedCount = logs.filter((log) => isNdtLogRejected(log)).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] w-[100vw] max-h-[100dvh] max-w-[100vw] flex-col gap-3 overflow-hidden rounded-none border-0 p-4 sm:p-5">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-heading text-blueprint-blue">
            NDT indications
            <span className="ml-2 text-sm font-semibold text-heritage-grey">
              {weldNumber || '—'} · {jobNumber || '—'}
            </span>
            <span className="ml-2 rounded-full bg-[rgba(0,39,63,0.06)] px-2.5 py-0.5 text-sm font-semibold text-heritage-grey">
              {logs.length}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs text-heritage-grey">
            {rejectedCount} rejected · {logs.length - rejectedCount} other — select a row for the full record
          </DialogDescription>
        </DialogHeader>

        {logs.length === 0 ? (
          <p className="text-sm text-heritage-grey">No NDT indication logs for this weld.</p>
        ) : (
          <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-5">
            <div className="flex min-h-0 flex-col overflow-hidden lg:col-span-2">
              <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-[#E2E5EA] bg-white">
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                    <tr>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Pass</th>
                      <th className="px-3 py-2">Evaluation</th>
                      <th className="px-3 py-2">Defect</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, index) => {
                      const key = ndtIndicationRowKey(log, index);
                      const selectedRow = key === selectedKey;
                      return (
                        <tr
                          key={key}
                          data-testid="ndt-indication-row"
                          className={clsx(
                            'cursor-pointer border-t border-[#E2E5EA]',
                            selectedRow
                              ? 'bg-[rgba(0,102,179,0.08)]'
                              : 'hover:bg-[rgba(0,39,63,0.04)]',
                          )}
                          onClick={() => setSelectedKey(key)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setSelectedKey(key);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label={`View NDT indication ${text(log.defectType)}`}
                        >
                          <td className="px-3 py-2 text-text-grey">{text(log.date)}</td>
                          <td className="px-3 py-2 text-text-grey">{text(log.passName)}</td>
                          <td className="px-3 py-2 text-text-grey">{text(log.evaluation)}</td>
                          <td className="px-3 py-2 font-medium text-blueprint-blue">{text(log.defectType)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex min-h-0 flex-col lg:col-span-3">
              <NdtIndicationDetailPanel log={selected} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
